/* Mr. Merchant — notched section shapes.
   Markup:
     <div class="mm-shape" data-mm-shape>
       <div class="mm-shape__body">…</div>
       <div class="mm-shape__slot mm-shape__slot--l">title</div>
       <div class="mm-shape__slot mm-shape__slot--r">controls</div>   (optional)
     </div>
   The body gets a clip-path with a notch under each slot (slot size + gap), so the page
   background shows around the slot's own black shape — same geometry as the approved mockup. */
(() => {
  if (window.mmShapes) return;

  // segs = top edge as [x0, x1, y] runs: y = d is notched, y = 0 is full height
  function shapePath(w, h, segs, r, br) {
    const y0 = segs[0][2];
    const p = [`M0 ${y0 + r}`, `Q0 ${y0} ${r} ${y0}`];
    for (let i = 0; i < segs.length - 1; i++) {
      const x = segs[i][1], a = segs[i][2], b = segs[i + 1][2], k = b < a ? -1 : 1;
      p.push(`L${x - r} ${a}`, `Q${x} ${a} ${x} ${a + k * r}`, `L${x} ${b - k * r}`, `Q${x} ${b} ${x + r} ${b}`);
    }
    const yl = segs[segs.length - 1][2];
    p.push(`L${w - r} ${yl}`, `Q${w} ${yl} ${w} ${yl + r}`);
    if (br) {
      // rounded notch cut into the bottom-right corner (bw x bh)
      const x = w - br[0], y = h - br[1];
      p.push(`L${w} ${y - r}`, `Q${w} ${y} ${w - r} ${y}`, `L${x + r} ${y}`, `Q${x} ${y} ${x} ${y + r}`, `L${x} ${h - r}`, `Q${x} ${h} ${x - r} ${h}`);
    } else {
      p.push(`L${w} ${h - r}`, `Q${w} ${h} ${w - r} ${h}`);
    }
    p.push(`L${r} ${h}`, `Q0 ${h} 0 ${h - r}`, 'Z');
    return p.join(' ');
  }

  function layout(el) {
    const body = el.querySelector(':scope > .mm-shape__body');
    if (!body) return;
    const cs = getComputedStyle(el);
    const g = parseFloat(cs.getPropertyValue('--g')) || 12;
    const radius = parseFloat(cs.getPropertyValue('--mm-shape-radius')) || 28;
    const L = el.querySelector(':scope > .mm-shape__slot--l');
    const R = el.querySelector(':scope > .mm-shape__slot--r');
    // slots that are hidden / statically placed (phones) do not cut the shape
    const cuts = (s) => s && s.offsetParent !== null && getComputedStyle(s).position === 'absolute' && getComputedStyle(s).getPropertyValue('--mm-nocut').trim() !== '1';
    const nl = cuts(L) ? L.offsetWidth + g : 0;
    const nr = cuts(R) ? R.offsetWidth + g : 0;
    const d = Math.max(cuts(L) ? L.offsetHeight : 0, cuts(R) ? R.offsetHeight : 0) + g;
    el.style.setProperty('--mm-notch', nl || nr ? `${d}px` : '0px');
    const w = body.offsetWidth, h = body.offsetHeight;
    if (!w || !h) return;
    const brCut = R && R.offsetParent !== null && getComputedStyle(R).getPropertyValue('--mm-cut-br').trim() === '1';
    const br = brCut ? [R.offsetWidth + g, R.offsetHeight + g] : null;
    if (!nl && !nr && !br) { body.style.clipPath = ''; return; }
    const r = Math.min(radius, d / 3);
    const segs = [];
    if (nl) segs.push([0, Math.min(nl, w - nr - 2 * r), d]);
    segs.push([nl ? Math.min(nl, w - nr - 2 * r) : 0, w - nr, 0]);
    if (nr) segs.push([w - nr, w, d]);
    body.style.clipPath = `path('${shapePath(w, h, segs, r, br)}')`;
  }

  // product cards: the label (badge) sits in a notch cut into the card's top-left corner (mockup: gap 5px)
  const CARDS = '.mm-products .card--card';
  function layoutCard(card) {
    const badge = [...card.querySelectorAll('.card__badge .badge')].find((b) => b.offsetWidth);
    const w = card.offsetWidth, h = card.offsetHeight;
    if (!badge || !w || !h) { card.style.removeProperty('--mm-card-clip'); return; }
    const c = card.getBoundingClientRect(), b = badge.getBoundingClientRect();
    const nl = b.right - c.left + 5, d = b.bottom - c.top + 5, r = Math.min(20, d / 3);
    card.style.setProperty('--mm-card-clip', `path('${shapePath(w, h, [[0, nl, d], [nl, w, 0]], r)}')`);
  }

  const all = () => {
    document.querySelectorAll('[data-mm-shape]').forEach(layout);
    document.querySelectorAll(CARDS).forEach(layoutCard);
  };
  let raf = 0;
  const queue = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(all); };
  const ro = new ResizeObserver(queue);
  const watch = (scope = document) => {
    scope.querySelectorAll('[data-mm-shape]').forEach((el) => { ro.observe(el); layout(el); });
    scope.querySelectorAll(CARDS).forEach((el) => { ro.observe(el); layoutCard(el); });
  };

  // sections fade/slide in as they scroll into view (CSS: .mm-reveal / .is-in; off for reduced motion)
  const io = 'IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches
    ? new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' })
    : null;
  const reveal = (scope = document) => {
    if (!io || window.Shopify?.designMode) return;
    // home: each mm section · custom pages: each block inside · other pages: each main section
    scope.querySelectorAll('.template-index .mm-section, .mm-footer, .mmc5 > div, .mmhp > .hero, .mmhp > .sec, .mmhs > .hero, .mmhs > .sec, '
      + 'body:not(.template-index) #MainContent > .shopify-section:not(:has(.mmc5, .mmhp, .mmhs, .mm-says))').forEach((el) => {
      if (el.classList.contains('mm-reveal')) return;
      el.classList.add('mm-reveal');
      io.observe(el);
    });
  };

  window.mmShapes = { layout, refresh: queue };
  reveal();
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => watch());
  else watch();
  document.fonts?.ready.then(queue);
  window.addEventListener('load', queue);
  document.addEventListener('shopify:section:load', (e) => watch(e.target));
})();

/* phone swipe rows: mark the centred card, add dots (only where the row scrolls sideways) */
(() => {
  const ROWS = '[id$="__new_arrivals"], [id$="__preorders"], [id$="__gundam"], [id$="__figures"]';
  const phone = matchMedia('(max-width: 749px)');
  function setup(grid) {
    if (grid.dataset.swipe) return;
    grid.dataset.swipe = '1';
    grid.classList.add('mm-swipe');
    const dots = document.createElement('div'); dots.className = 'mm-swipe-dots'; dots.setAttribute('aria-hidden', 'true');
    grid.after(dots);
    const update = () => {
      const items = [...grid.children].filter((li) => !li.hidden);
      if (dots.children.length !== items.length) dots.innerHTML = items.map(() => '<i></i>').join('');
      const mid = grid.getBoundingClientRect().left + grid.clientWidth / 2;
      let best = 0, bd = 1e9;
      items.forEach((li, i) => { const r = li.getBoundingClientRect(); const d = Math.abs(r.left + r.width / 2 - mid); if (d < bd) { bd = d; best = i; } });
      items.forEach((li, i) => li.classList.toggle('is-on', i === best));
      [...dots.children].forEach((d, i) => d.classList.toggle('on', i === best));
    };
    let raf = 0;
    grid.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    new MutationObserver(update).observe(grid, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
    update();
  }
  // product page only: the "More like this" products under an open product (all other grids stay grids)
  const OTHER = '.template-product .related-products ul.product-grid';
  const run = () => {
    if (!phone.matches) return;
    document.querySelectorAll(ROWS).forEach((s) => s.querySelectorAll('.mm-products__grid').forEach(setup));
    document.querySelectorAll(OTHER).forEach(setup);
  };
  document.addEventListener('DOMContentLoaded', () => {
    run();
    // rows that arrive later (related products are fetched after load)
    let t2 = 0;
    new MutationObserver(() => { clearTimeout(t2); t2 = setTimeout(run, 120); }).observe(document.body, { childList: true, subtree: true });
  });
  addEventListener('load', run); phone.addEventListener('change', run);
  // keep the right-hand buttons clear of the title on phones
  const measure = () => document.querySelectorAll('.mm-shape').forEach((s) => { const l = s.querySelector(':scope > .mm-shape__slot--l'); if (l) s.style.setProperty('--mm-l-w', l.offsetWidth + 'px'); });
  document.addEventListener('DOMContentLoaded', measure); addEventListener('load', measure); addEventListener('resize', measure);
})();

/* header logo on scroll: scrolling down hides the bar, the original mascot (no wordmark, no background) floats centred at the top;
   scrolling up brings the bar back; back at the top of the page the logo "lands" in its place in the bar */
(() => {
  const head = document.querySelector('.section-header');
  const link = document.querySelector('.header__heading-link');
  const src = document.querySelector('.mm-top-mascot img');
  if (!head || !link || !src) return;
  const fl = document.createElement('a');
  fl.className = 'mm-float-logo'; fl.href = link.getAttribute('href') || '/'; fl.setAttribute('aria-label', 'Home'); fl.tabIndex = -1;
  fl.innerHTML = `<img src="${src.currentSrc || src.src}" alt="" width="231" height="278">`;
  document.body.appendChild(fl);
  let wasAway = false;
  const sync = () => {
    const hidden = head.classList.contains('shopify-section-header-hidden');
    const away = head.classList.contains('scrolled-past-header');
    fl.classList.toggle('is-on', hidden);
    if (wasAway && !away) { link.classList.remove('mm-logo-land'); void link.offsetWidth; link.classList.add('mm-logo-land'); }
    wasAway = away;
  };
  new MutationObserver(sync).observe(head, { attributes: true, attributeFilter: ['class'] });
  sync();
})();
